/*
 * product-size-label-patch.js
 * ---------------------------------------------------------------------------
 * Standalone runtime patch for the product size picker unit labels
 * ("(Inches)", "(US)", "(CAD)", ...).
 *
 * Mirrors the if/else chain from:
 *   src/scripts/snippets/product-size-picker/product-size-picker.js
 *     -> loadSizeOption()
 *
 * Rules applied (in order):
 *   1. Dress Shirts + (Neck | Chest | Sleeve Length) -> "(Inches)"
 *   2. First size value contains "$"                 -> "(CAD)"
 *   3. First size value contains "W"                 -> "(Inches)"  (Waist — runs
 *      BEFORE the Size-category blank so single-dimension waist products like
 *      jeans with values "W32/W34" and category "Size" still get inches)
 *   4. Category name === "Size"                      -> ""          (strips legacy "(US)")
 *   5. Pants | Shorts | Jeans + (Length | Longueur)  -> "(Inches)"  (restores Length unit)
 *   6. Anything else                                  -> ""
 *
 * Idempotent. Handles initial render, color-swatch re-renders, and upsell
 * wrappers inside associated-upsells modules. Pure DOM work, no dependencies.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict'

  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (typeof window.MutationObserver !== 'function') return

  const WRAPPER_SELECTOR = '.size-options-wrapper'

  function resolveLabel (productType, categoryName, firstSizeValue) {
    if (!categoryName) return null

    // 1. Dress Shirts neck/chest/sleeve → (Inches)
    if (
      productType &&
      productType.indexOf('Dress Shirts') !== -1 &&
      (
        categoryName === 'Neck Size' ||
        categoryName === 'Chest Size' ||
        categoryName === 'Sleeve Length'
      )
    ) {
      return '(Inches)'
    }

    // 2. Currency sniff
    if (firstSizeValue && firstSizeValue.indexOf('$') !== -1) return '(CAD)'

    // 3. Waist sniff (values like "W32") — runs BEFORE the Size-category blank
    //    so single-dimension waist products (jeans w/ only W32/W34, category "Size") get inches
    if (firstSizeValue && firstSizeValue.indexOf('W') !== -1) return '(Inches)'

    // 4. Category "Size" → blank (strips any legacy "(US)")
    if (categoryName === 'Size') return ''

    // 5. Pants / Shorts / Jeans Length → (Inches)
    if (
      productType &&
      (
        productType.indexOf('Pants') !== -1 ||
        productType.indexOf('Shorts') !== -1 ||
        productType.indexOf('Jeans') !== -1
      ) &&
      (categoryName === 'Length' || categoryName === 'Longueur')
    ) {
      return '(Inches)'
    }

    // 6. Default: blank
    return ''
  }

  function getFirstSizeValue (labelEl) {
    let sibling = labelEl.nextElementSibling
    while (sibling && !(sibling.classList && sibling.classList.contains('size-options'))) {
      sibling = sibling.nextElementSibling
    }
    if (!sibling) return ''
    const firstOption = sibling.querySelector('.size-option')
    if (!firstOption) return ''
    return firstOption.getAttribute('data-size-value') || firstOption.textContent || ''
  }

  function patchLabel (labelEl, wrapperEl) {
    if (!labelEl || !wrapperEl) return
    const strong = labelEl.querySelector('strong')
    const span = labelEl.querySelector('span')
    if (!strong || !span) return

    const categoryName = (strong.textContent || '').trim()
    const productType = wrapperEl.getAttribute('data-product-type') || ''
    const firstSizeValue = getFirstSizeValue(labelEl)

    const desired = resolveLabel(productType, categoryName, firstSizeValue)
    if (desired === null) return
    if (span.innerHTML !== desired) span.innerHTML = desired
  }

  function patchWrapper (wrapperEl) {
    if (!wrapperEl) return
    // Only direct-child <p> labels — size-option children have their own structure
    const children = wrapperEl.children
    for (let i = 0; i < children.length; i++) {
      if (children[i].tagName === 'P') patchLabel(children[i], wrapperEl)
    }
  }

  function patchAll (root) {
    const scope = root && root.querySelectorAll ? root : document
    const wrappers = scope.querySelectorAll(WRAPPER_SELECTOR)
    for (let i = 0; i < wrappers.length; i++) patchWrapper(wrappers[i])
  }

  function handleMutation (mutations) {
    for (let m = 0; m < mutations.length; m++) {
      const mut = mutations[m]
      if (mut.type !== 'childList') continue

      // Fast path: a wrapper's children changed directly (label or .size-options appended)
      const target = mut.target
      if (target && target.classList && target.classList.contains('size-options-wrapper')) {
        patchWrapper(target)
        continue
      }

      // Scan added nodes for newly-inserted wrappers
      const added = mut.addedNodes
      for (let a = 0; a < added.length; a++) {
        const node = added[a]
        if (!node || node.nodeType !== 1) continue

        if (node.classList && node.classList.contains('size-options-wrapper')) {
          patchWrapper(node)
        } else if (node.querySelectorAll) {
          const nested = node.querySelectorAll(WRAPPER_SELECTOR)
          for (let n = 0; n < nested.length; n++) patchWrapper(nested[n])
        }
      }
    }
  }

  function init () {
    patchAll(document)
    const observer = new window.MutationObserver(handleMutation)
    observer.observe(document.body, { childList: true, subtree: true })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
